import { observable, action, decorate } from 'mobx';
import api from "../../utils/api";
import { DataResponse, GenericResponse } from "../models/genericResponseModels";
import { Department } from '../models/licenseModels';

export interface ReviewStats {
    id: string;
    isCompleted: boolean;
    startDate: Date;
    endDate: Date;
    totalReviews: number;
    reviewsCompleted: number;
    department: string;
}

export interface LicenseDataEmployeeDTO {
    name: string;
    email: string;
    title: string;
}

export interface LicenseDataPreviewDTO {
    managerName: string;
    managerEmail: string;
    employees: LicenseDataEmployeeDTO[];
}

class LicenseReviewStore {
    review: ReviewStats | null = null;
    reviewProcessInProgress: boolean = false;
    notificationMessage: string | null = null;
    notificationSeverity: 'error' | 'info' = 'info';
    createReviewsLoading: boolean = false;
    fetchError: string | null = null;
    loading: boolean = false;
    departments: Department[] = [];
    selectedDepartmentId: string | null = null;
    selectedDepartmentName: string | null = null;
    activeReviews: ReviewStats[] = [];
    previewData: LicenseDataPreviewDTO[] = [];
    loadingPreviewData: boolean = false;
    previewError: string | null = null;

    setReview(data: ReviewStats) {
        this.review = data;
    }

    setReviewProcessInProgress(value: boolean) {
        this.reviewProcessInProgress = value;
    }

    setNotificationMessage(message: string | null, severity: 'error' | 'info' = 'info') {
        this.notificationMessage = message;
        this.notificationSeverity = severity;
    }

    setCreateReviewsLoading(value: boolean) {
        this.createReviewsLoading = value;
    }

    setFetchError(error: string | null) {
        this.fetchError = error;
    }

    setLoading(value: boolean) {
        this.loading = value;
    }

    setSelectedDepartment(departmentId: string) {
        this.selectedDepartmentId = departmentId;
        const department = this.departments.find(d => d.id === departmentId);
        this.selectedDepartmentName = department ? department.name : null;
    }

    setPreviewData(data: LicenseDataPreviewDTO[]) {
        this.previewData = data;
    }

    setLoadingPreviewData(value: boolean) {
        this.loadingPreviewData = value;
    }

    setPreviewError(error: string | null) {
        this.previewError = error;
    }

    isAuditCompleted() {
        return this.review?.isCompleted ?? false;
    }

    isReviewsCompleted() {
        return this.review?.totalReviews === this.review?.reviewsCompleted;
    }

    async fetchDepartments() {
        try {
            const response = await api.get<DataResponse<Department[]>>('/License/GetDepartments');
            if (response.data.isSuccessful && Array.isArray(response.data.data)) {
                this.departments = response.data.data;
                if (response.data.data.length > 0 && !this.selectedDepartmentId) {
                    const initialDept = response.data.data[0];
                    this.selectedDepartmentId = initialDept.id;
                    this.selectedDepartmentName = initialDept.name;
                }
            }
        } catch (error) {
            this.setFetchError('Failed to fetch departments');
        }
    }

    async fetchAllActiveReviews() {
        this.setLoading(true);
        try {
            const response = await api.get<DataResponse<ReviewStats[]>>(
                '/License/GetAllActiveLicenseAudits'
            );
            if (response.data.isSuccessful) {
                this.activeReviews = response.data.data;
                this.setFetchError(null);
            } else {
                this.setFetchError('Failed to fetch active reviews.');
            }
        } catch (error) {
            this.setFetchError('An error occurred while fetching active reviews.');
        } finally {
            this.setLoading(false);
        }
    }

    async fetchLicenseDataPreview(departmentId: string) {
        if (!departmentId) return;

        this.setLoadingPreviewData(true);
        this.setPreviewError(null);

        try {
            const response = await api.get<DataResponse<LicenseDataPreviewDTO[]>>(
                `/License/GetLicenseDataPreview?departmentId=${departmentId}`
            );

            if (response.data.isSuccessful && Array.isArray(response.data.data)) {
                this.setPreviewData(response.data.data);
            } else {
                this.setPreviewError('Failed to fetch preview data');
            }
        } catch (error) {
            this.setPreviewError('An error occurred while fetching preview data');
        } finally {
            this.setLoadingPreviewData(false);
        }
    }

    departmentHasActiveReview(departmentName: string) {
        return this.activeReviews.some(ar => ar.department === departmentName && !ar.isCompleted);
    }

    async createReviews() {
        if (!this.selectedDepartmentId) return;

        this.setCreateReviewsLoading(true);
        try {
            const response = await api.get<GenericResponse>(
                `/License/CreateLicenseReviews?departmentId=${this.selectedDepartmentId}`
            );
            if (response.data.isSuccessful) {
                this.setNotificationMessage('Reviews created successfully.');
                await this.fetchAllActiveReviews();
            } else {
                this.setNotificationMessage('Failed to create reviews.', 'error');
            }
        } catch (error) {
            this.setNotificationMessage('An error occurred while creating reviews.', 'error');
        } finally {
            this.setCreateReviewsLoading(false);
        }
    }

    async markReviewProcessAsCompleted(reviewId: string) {
        try {
            const response = await api.get<GenericResponse>(
                `/License/SetLicenseAuditAsCompleted?auditId=${reviewId}`
            );

            if (response.data.isSuccessful) {
                this.setNotificationMessage('Review process marked as completed successfully.');
                await this.fetchAllActiveReviews();
            } else {
                this.setNotificationMessage('Failed to mark review process as completed.', 'error');
            }
        } catch (error) {
            this.setNotificationMessage('An error occurred while marking the review process as completed.', 'error');
        }
    }
}

decorate(LicenseReviewStore, {
    review: observable,
    reviewProcessInProgress: observable,
    notificationMessage: observable,
    notificationSeverity: observable,
    createReviewsLoading: observable,
    fetchError: observable,
    loading: observable,
    setReview: action,
    setReviewProcessInProgress: action,
    setNotificationMessage: action,
    setCreateReviewsLoading: action,
    setFetchError: action,
    setLoading: action,
    isAuditCompleted: action,
    isReviewsCompleted: action,
    markReviewProcessAsCompleted: action,
    createReviews: action,
    departments: observable,
    selectedDepartmentId: observable,
    selectedDepartmentName: observable,
    activeReviews: observable,
    fetchDepartments: action,
    setSelectedDepartment: action,
    fetchAllActiveReviews: action,
    departmentHasActiveReview: action,
    previewData: observable,
    loadingPreviewData: observable,
    previewError: observable,
    setPreviewData: action,
    setLoadingPreviewData: action,
    setPreviewError: action,
    fetchLicenseDataPreview: action,
});

const licenseReviewStore = new LicenseReviewStore();
export default licenseReviewStore;